<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import { HugeIcon, TSkeleton, TSuspense } from '@teachino/shared-design-system';
import { $t } from '@teachino/shared-i18n';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { useQuery } from '@urql/vue';
import { computed, ref } from 'vue';
import { availablePlansQuery } from './available-plans.query';
import { type BillingCycle } from './request-checkout-session-data.mutation';
import TraineeUpgradeButton from './TraineeUpgradeButton.vue';
import UpgradeFeatureListItem from './UpgradeFeatureListItem.vue';
import { UPGRADE_PAGE_FEATURES } from './upgrade-page-features';
import { useLicense } from './useLicense';

const HERO_ICON_SIZE = 24;

const UPGRADE_PAGE_BILLING_CYCLES: BillingCycle[] = [
  'HALF_YEARLY',
  'YEARLY',
  'TWO_YEARS',
];

const { openProLicenseCheckout } = useLicense();
const di = useGlobalDependencyProvider();
const analytics = di.inject('analytics');

const availablePlans = useQuery({ query: availablePlansQuery });
const loadingBillingCycle = ref<BillingCycle | null>(null);

type AvailablePlanSelectOption = {
  label: string;
  price: string;
  tag?: string;
  tagVariant?: 'success' | 'quarternary';
  billingCycle: BillingCycle;
  isRecommended: boolean;
};

const buyPro = async (option: AvailablePlanSelectOption): Promise<void> => {
  loadingBillingCycle.value = option.billingCycle;
  analytics.track('upgrade-start-payment', { source: 'upgrade-page' });
  await openProLicenseCheckout({
    billingCycle: option.billingCycle,
  });
  loadingBillingCycle.value = null;
};

const availablePlanSelectOptions = computed<AvailablePlanSelectOption[]>(() => {
  const prices =
    availablePlans.data.value?.userPlans.flatMap(
      (option) => option.defaultPriceMonth ?? 0,
    ) ?? [];
  const basePrice = Math.max(...prices);
  /* eslint-disable @typescript-eslint/naming-convention */
  const billingCycleMonthMap: Record<BillingCycle, string> = {
    HALF_YEARLY: '6',
    NONE: '0',
    YEARLY: '12',
    MONTHLY: '1',
    TWO_YEARS: '24',
  };
  /* eslint-enable @typescript-eslint/naming-convention */
  const options: AvailablePlanSelectOption[] =
    availablePlans.data.value?.userPlans
      .filter(
        (plan) =>
          plan.type === 'PAID' &&
          UPGRADE_PAGE_BILLING_CYCLES.includes(plan.billingCycle),
      )
      .map((plan) => {
        const label =
          plan.billingCycle === 'MONTHLY'
            ? $t('upgrade.buy.payment_cycle.option_one')
            : $t('upgrade.buy.payment_cycle.option_multiple', [
                {
                  id: 'months',
                  value: billingCycleMonthMap[plan.billingCycle],
                },
              ]);

        const priceInCents = plan.defaultPriceMonth ?? 0;
        const price = $t('upgrade.buy.payment_cycle.price', [
          { id: 'price', value: `${String(priceInCents / 100)}€` },
        ]);

        const isRecommended = plan.billingCycle === 'YEARLY';

        const planOption: AvailablePlanSelectOption = {
          label,
          price,
          billingCycle: plan.billingCycle,
          isRecommended,
        };

        if (isRecommended) {
          planOption.tag = $t('upgrade.buy.tag.loved');
          planOption.tagVariant = 'success';
        }

        if (plan.billingCycle === 'TWO_YEARS') {
          const discountPercent =
            plan.defaultPriceMonth === basePrice
              ? undefined
              : Math.round((1 - priceInCents / basePrice) * 100);
          if (discountPercent) {
            planOption.tag = $t('upgrade.buy.tag.cheapest', [
              { id: 'percent', value: String(discountPercent) },
            ]);
            planOption.tagVariant = 'quarternary';
          }
        }

        return planOption;
      })
      .sort((a, b) => {
        const aCycle = +billingCycleMonthMap[a.billingCycle];
        const bCycle = +billingCycleMonthMap[b.billingCycle];
        return aCycle - bCycle;
      }) ?? [];
  return options;
});

const tagClassByVariant = {
  success: 'bg-success-50 text-success-700',
  quarternary: 'bg-quarternary-50 text-quarternary-700',
} as const;

const planCardClass = (option: AvailablePlanSelectOption): string[] => [
  'group flex h-full w-full flex-col overflow-hidden rounded-xl border text-center transition-all',
  'cursor-pointer hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
  'disabled:cursor-not-allowed disabled:opacity-50',
  option.isRecommended
    ? 'border-primary-300 bg-white shadow-md ring-1 ring-primary-200 hover:border-primary-400 md:-mt-1 md:mb-1'
    : 'border-slate-200 bg-white shadow-sm hover:border-slate-300',
];

const planCardBodyClass = (option: AvailablePlanSelectOption): string =>
  option.isRecommended
    ? 'flex flex-1 flex-col p-5 pb-4'
    : 'flex flex-1 flex-col p-4 pb-4';

const planCtaFooterClass = (option: AvailablePlanSelectOption): string[] => [
  'mt-auto w-full px-4 py-3 text-sm font-semibold text-white transition-all',
  option.isRecommended
    ? 'bg-gradient-to-tr from-violet-600 to-indigo-600 group-hover:brightness-110'
    : 'bg-violet-600 group-hover:bg-violet-700',
];

const isPlanLoading = (billingCycle: BillingCycle): boolean =>
  loadingBillingCycle.value === billingCycle;

const planAriaLabel = (option: AvailablePlanSelectOption): string =>
  `${option.label}, ${option.price}, ${$t('upgrade.plan.cta')}`;
</script>

<template>
  <section
    class="w-full overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm"
  >
    <div
      class="upgrade-hero-gradient border-b border-slate-200/60 px-5 py-4 text-center md:px-6 md:py-5"
    >
      <div
        class="border-primary-100 text-primary-600 mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl border bg-white shadow-sm"
      >
        <HugeIcon icon="crown" :size="HERO_ICON_SIZE" />
      </div>
      <h2 class="text-lg font-semibold text-slate-900 md:text-xl">
        {{ $t('upgrade.license_page.pro_offer_title') }}
      </h2>
      <p class="mx-auto mt-1.5 max-w-lg text-sm text-slate-600">
        {{ $t('upgrade.license_page.pro_offer_subtitle') }}
      </p>
    </div>

    <TSuspense :response="availablePlans">
      <template #default>
        <div class="flex flex-col gap-5 px-5 py-4 md:px-6 md:py-5">
          <div class="space-y-3">
            <h3 class="text-center text-sm font-semibold text-slate-900">
              {{ $t('upgrade.page.pricing_heading') }}
            </h3>
            <div class="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-3">
              <button
                v-for="option in availablePlanSelectOptions"
                :key="option.label"
                type="button"
                :class="planCardClass(option)"
                :disabled="loadingBillingCycle !== null"
                :aria-busy="isPlanLoading(option.billingCycle)"
                :aria-label="planAriaLabel(option)"
                @click="void buyPro(option)"
              >
                <div :class="planCardBodyClass(option)">
                  <div class="mb-3 flex min-h-6 justify-center">
                    <span
                      v-if="option.tag && option.tagVariant"
                      class="rounded-full px-2.5 py-0.5 text-xs font-semibold"
                      :class="tagClassByVariant[option.tagVariant]"
                    >
                      {{ option.tag }}
                    </span>
                  </div>
                  <p class="text-sm font-medium text-slate-600">
                    {{ option.label }}
                  </p>
                  <p
                    class="mt-2 text-2xl font-semibold tracking-tight text-slate-900"
                  >
                    {{ option.price }}
                  </p>
                </div>
                <div :class="planCtaFooterClass(option)">
                  <span class="inline-flex items-center justify-center gap-1.5">
                    <HugeIcon
                      v-if="isPlanLoading(option.billingCycle)"
                      icon="loading"
                      :spin="true"
                      :size="16"
                    />
                    {{ $t('upgrade.plan.cta') }}
                  </span>
                </div>
              </button>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
            <UpgradeFeatureListItem
              v-for="feature in UPGRADE_PAGE_FEATURES"
              :key="feature.titleKey"
              :icon="feature.icon"
              :title="$t(feature.titleKey)"
              :description="$t(feature.descriptionKey)"
            />
          </div>

          <div
            class="space-y-3 border-t border-slate-100 pt-4 text-center text-sm text-slate-500"
          >
            <p
              class="leading-relaxed"
              v-html="$t('upgrade.license_page.legal_billing')"
            ></p>

            <div
              class="upgrade-hero-gradient mx-auto max-w-xl rounded-xl border border-slate-200/80 px-4 py-4 text-center text-sm text-slate-700"
            >
              <p
                class="leading-relaxed"
                v-html="$t('upgrade.license_page.trainee_discount')"
              ></p>
              <div class="mt-3">
                <TraineeUpgradeButton source="upgrade-page" />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #fetching>
        <div class="flex flex-col gap-4 px-5 py-6">
          <TSkeleton size="medium" width="full" />
          <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TSkeleton
              v-for="index in 3"
              :key="index"
              size="large"
              width="full"
            />
          </div>
        </div>
      </template>
    </TSuspense>
  </section>
</template>

<style scoped>
.upgrade-hero-gradient {
  background-image: linear-gradient(
    114deg,
    rgba(255, 125, 69, 0.08) 8%,
    rgba(255, 146, 71, 0.08) 30%,
    rgba(183, 102, 252, 0.08) 59%,
    rgba(81, 242, 252, 0.08) 95%
  );
}
</style>
